

import React, { useState } from 'react';
import cn from 'classnames';

import type { PreviewBrandingImagePage } from './PreviewPages';
import styles from './trip-alert-branding.module.css';
import LoadingSpinner from '../../../components/LoadingSpinner';
import Alert from '../../../components/alert';
import { CurrentImage } from './CurrentImage';
import { ImageGuidelines } from './ImageGuidelines';
import { ImageUpload } from './ImageUpload';
import { DisplayImage } from './DisplayImage';
import { PreviewPages } from './PreviewPages';
import { ButtonPrimary } from '../../../components/forms/Buttons';
import { Modal, type ModalProps } from '../../../components/modal/Modal';

type TripAlertBrandingProps = {
  isCurrentImageLoading: boolean,
  hasCurrentImageError: boolean,
  onImageAccepted: (file: File) => void,
  onImageRejected: () => void,
  hasPreviewError: boolean,
  onSave: () => void,
  successfulSave: boolean,
  failedSave: boolean,
  currentImage?: string,
  uploadFile?: File,
  previewBrandingImagePages?: PreviewBrandingImagePage[],
  previewImage?: string,
};

const SaveModal: React.FC<ModalProps> = ({ isOpen, onClose, onSubmit, title }) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    onSubmit={onSubmit}
    title={title}
  >
    This will replace the current header image for all traveler facing Trip
    Alert pages. The current image will be permanently deleted.
  </Modal>
);

export const TripAlertBranding: React.FC<TripAlertBrandingProps> = ({
  isCurrentImageLoading,
  currentImage,
  hasCurrentImageError,
  uploadFile,
  onImageAccepted,
  onImageRejected,
  previewImage,
  hasPreviewError,
  previewBrandingImagePages,
  onSave,
  successfulSave,
  failedSave,
}) => {
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);

  return (
    <div className={cn(styles.root, 'container-fluid')}>
      <div className="row">
        <div className="bodycard-wrapper">
          {successfulSave && (
            <Alert type="success" message="Save successful." />
          )}
          <h3>{"Add your brand's image to the traveler's Trip Alert pages"}</h3>
        </div>
        <div className="col-md-6">
          <h4>Current image</h4>
          {isCurrentImageLoading && (
            <LoadingSpinner className={styles.loadingSpinner} />
          )}
          {hasCurrentImageError && (
            <Alert type="danger" message="Unable to load current image." />
          )}
          {!isCurrentImageLoading && !hasCurrentImageError && (
            <CurrentImage src={currentImage} />
          )}
          <h4 style={{ paddingTop: 20 }}>Image guidelines:</h4>
          <ImageGuidelines />
          <h4>Header image</h4>
          <ImageUpload
            file={uploadFile}
            onImageAccepted={onImageAccepted}
            onImageRejected={onImageRejected}
          />
          {previewImage && (
            <>
              <h4>Image Preview</h4>
              <DisplayImage src={previewImage} />
            </>
          )}
          {previewImage && (
            <h4>The image will appear on the following pages:</h4>
          )}
          {previewImage && !previewBrandingImagePages && !hasPreviewError && (
            <LoadingSpinner className={styles.loadingSpinner} />
          )}
          {hasPreviewError && (
            <div className={cn(styles.message, 'alert-danger')}>
              Unable to create preview pages. Please upload the image again.
            </div>
          )}
          {previewBrandingImagePages && (
            <>
              <PreviewPages
                previewBrandingImagePages={previewBrandingImagePages}
              />
              <div className={cn(styles.save)}>
                <ButtonPrimary
                  id="saveButton"
                  onClick={() => setIsSaveModalOpen(true)}
                >
                  Save
                </ButtonPrimary>
              </div>
              {failedSave && (
                <div
                  className={cn(
                    styles.message,
                    'alert-danger',
                    styles.failedSaveError,
                  )}
                >
                  Failed to save.
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <SaveModal
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        onSubmit={() => {
          onSave();
          setIsSaveModalOpen(false);
        }}
        title={'Confirm Save'}
      />
    </div>
  );
};
